"use client";

import React, { useRef, useState } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { QrCode, Zap, ShieldCheck, Server, Users, WifiOff } from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

// --- HELPER COMPONENTS ---

function QRPulse() {
  const containerRef = useRef(null);
  const qrRef = useRef(null);
  useGSAP(
    () => {
      gsap.to(qrRef.current, {
        scale: 1.15,
        duration: 0.8,
        ease: "power1.inOut",
        yoyo: true,
        repeat: -1,
      });
    },
    { scope: containerRef },
  );
  return (
    <div ref={containerRef} className="flex items-center justify-center h-full">
      <div ref={qrRef} className="relative">
        <div className="absolute inset-0 bg-orange-500/20 blur-xl rounded-full" />
        <QrCode className="w-20 h-20 text-white relative z-10" />
      </div>
    </div>
  );
}

function LatencyMeter() {
  const containerRef = useRef(null);
  useGSAP(
    () => {
      const tl = gsap.timeline({ repeat: -1, repeatDelay: 1 });
      tl.set(".text-result", { opacity: 0, display: "none" })
        .set(".text-syncing", { opacity: 1, display: "block" })
        .set(".progress-bar", { width: "0%" })
        .to(".progress-bar", { width: "100%", duration: 0.8, ease: "circ.out" })
        .to(".text-syncing", { opacity: 0, duration: 0.2 })
        .set(".text-syncing", { display: "none" })
        .set(".text-result", {
          display: "flex",
          opacity: 0,
          y: 10,
          filter: "blur(5px)",
        })
        .to(".text-result", {
          opacity: 1,
          y: 0,
          filter: "blur(0px)",
          duration: 0.4,
        });
    },
    { scope: containerRef },
  );
  return (
    <div
      ref={containerRef}
      className="flex flex-col items-center justify-center h-full gap-4 w-full px-8"
    >
      <div className="h-10 flex items-center justify-center relative w-full">
        <div className="text-syncing text-orange-500 font-mono text-sm tracking-widest uppercase">
          Syncing...
        </div>
        <div className="text-result hidden items-center gap-2 text-4xl font-black text-white">
          <Zap className="w-6 h-6 text-orange-500 fill-orange-500" /> 98ms
        </div>
      </div>
      <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden">
        <div className="progress-bar h-full bg-orange-500 rounded-full w-0" />
      </div>
      <span className="text-xs text-gray-500 uppercase tracking-wider font-bold">
        Notification Latency
      </span>
    </div>
  );
}

function ShieldWall() {
  const containerRef = useRef(null);
  useGSAP(
    () => {
      const shields = gsap.utils.toArray(".shield-item");
      const icons = gsap.utils.toArray(".shield-icon");
      const tl = gsap.timeline({ repeat: -1, repeatDelay: 0.5 });
      tl.to(shields, {
        backgroundColor: "rgba(249, 115, 22, 0.2)",
        borderColor: "rgba(249, 115, 22, 1)",
        scale: 1.1,
        duration: 0.3,
        stagger: 0.2,
        ease: "back.out(1.7)",
      })
        .to(icons, { color: "#f97316", duration: 0.3, stagger: 0.2 }, "<")
        .to(
          [shields, icons],
          {
            backgroundColor: "rgba(255, 255, 255, 0.05)",
            borderColor: "rgba(255, 255, 255, 0.05)",
            color: "#374151",
            scale: 1,
            duration: 0.4,
            clearProps: "all",
          },
          "+=0.5",
        );
    },
    { scope: containerRef },
  );
  return (
    <div
      ref={containerRef}
      className="flex items-center justify-center h-full gap-4"
    >
      {[1, 2, 3].map((id) => (
        <div
          key={id}
          className="shield-item w-14 h-14 rounded-2xl flex items-center justify-center border border-white/5 bg-white/5"
        >
          <ShieldCheck className="shield-icon w-6 h-6 text-gray-700 transition-none" />
        </div>
      ))}
    </div>
  );
}

function ServerPulse() {
  const containerRef = useRef(null);
  useGSAP(
    () => {
      gsap.to(".pulse-ring", {
        scale: 2.5,
        opacity: 0,
        duration: 2,
        stagger: { each: 0.6, repeat: -1 },
        ease: "power1.out",
      });
    },
    { scope: containerRef },
  );
  return (
    <div
      ref={containerRef}
      className="flex items-center justify-center h-full relative"
    >
      <div className="relative z-10 bg-[#0A0A0A] p-4 rounded-full border border-white/10">
        <Server className="w-10 h-10 text-white" />
      </div>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="pulse-ring absolute w-16 h-16 border border-orange-500/30 rounded-full"
        />
      ))}
    </div>
  );
}

function MemberGrid() {
  const containerRef = useRef(null);
  const [layoutClass, setLayoutClass] = useState("grid-cols-2");
  const layouts = ["grid-cols-2", "grid-cols-4"];
  const layoutIndex = useRef(0);

  useGSAP(
    () => {
      const tl = gsap.timeline({ repeat: -1, repeatDelay: 2 });
      tl.to(".grid-container", {
        opacity: 0,
        scale: 0.9,
        duration: 0.3,
        onComplete: () => {
          layoutIndex.current = (layoutIndex.current + 1) % layouts.length;
          setLayoutClass(layouts[layoutIndex.current]);
        },
      }).to(".grid-container", {
        opacity: 1,
        scale: 1,
        duration: 0.5,
        ease: "back.out(1.2)",
      });
    },
    { scope: containerRef },
  );

  return (
    <div
      ref={containerRef}
      className="h-45 w-full flex items-center justify-center px-6 overflow-hidden relative"
    >
      <div
        className={`grid-container grid ${layoutClass} gap-2 w-full transition-none`}
      >
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="bg-white/10 rounded-lg h-16 w-full flex items-center justify-center"
          >
            <Users className="w-4 h-4 text-white/30" />
          </div>
        ))}
      </div>
    </div>
  );
}

// --- MAIN COMPONENT ---

const FeaturesSection = () => {
  const containerRef = useRef(null);

  useGSAP(
    () => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 75%",
          toggleActions: "play none none reverse",
        },
      });
      tl.from(".feature-title", {
        y: 50,
        opacity: 0,
        duration: 0.8,
        ease: "power3.out",
      })
        .from(
          ".feature-desc",
          { y: 20, opacity: 0, duration: 0.6, ease: "power2.out" },
          "-=0.4",
        )
        .from(
          ".bento-card",
          {
            y: 60,
            opacity: 0,
            duration: 0.8,
            stagger: 0.15,
            ease: "power2.out",
          },
          "-=0.2",
        );
    },
    { scope: containerRef },
  );

  return (
    <section
      ref={containerRef}
      className="bg-[#050505] px-4 py-16 md:py-32 min-h-screen flex flex-col items-center justify-center relative border-t border-white/5"
    >
      <div className="max-w-7xl w-full mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="feature-title text-5xl md:text-5xl font-clash font-bold text-white mb-6 leading-tight">
            <span className="text-transparent bg-clip-text bg-linear-to-r from-orange-500 to-amber-500">
              Intelligent{" "}
            </span>
            Nervous System by Design.
          </h2>
          <p className="feature-desc text-gray-400 max-w-2xl mx-auto text-lg">
            Smart Dwara isn&apos;t just an app; it&apos;s a living system that
            reacts, protects, and organizes your life in real-time.
          </p>
        </div>

        <div className="features-grid grid grid-cols-1 md:grid-cols-6 gap-4 auto-rows-auto md:auto-rows-[240px]">
          {/* 1. Flash Pass */}
          <div className="bento-card md:col-span-2 md:row-span-2 bg-[#0A0A0A] border border-white/5 rounded-4xl p-8 flex flex-col hover:border-orange-500/30 transition-colors group overflow-hidden relative min-h-75 md:min-h-0">
            <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity">
              <QrCode className="w-32 h-32 text-orange-500" />
            </div>
            <div className="flex-1">
              <QRPulse />
            </div>
            <div className="mt-4 relative z-10">
              <h3 className="text-2xl text-white font-clash font-semibold mb-2">
                Flash Entry
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Generate dynamic QR codes that expire automatically. Grant
                access in seconds.
              </p>
            </div>
          </div>

          {/* 2. Real-time Alerts */}
          <div className="bento-card md:col-span-2 bg-[#0A0A0A] border border-white/5 rounded-4xl p-8 flex flex-col hover:border-orange-500/30 transition-colors group min-h-60">
            <div className="flex-1 flex items-center">
              <LatencyMeter />
            </div>
            <div className="mt-4">
              <h3 className="text-xl text-white font-clash font-semibold mb-1">
                Zero Latency
              </h3>
              <p className="text-gray-400 text-sm">
                Instant notifications via WebSocket.
              </p>
            </div>
          </div>

          {/* 3. Smart Management */}
          <div className="bento-card md:col-span-2 md:row-span-2 bg-[#0A0A0A] border border-white/5 rounded-4xl p-8 flex flex-col hover:border-orange-500/30 transition-colors group overflow-hidden h-122.5 md:min-h-0">
            <div className="flex-1 flex items-center justify-center">
              <MemberGrid />
            </div>
            <div className="mt-4 relative z-20 bg-[#0A0A0A]/80 backdrop-blur-sm rounded-lg">
              <h3 className="text-xl text-white font-clash font-semibold mb-2 flex items-center gap-2">
                <Users className="w-5 h-5 text-orange-500" /> Smart Manage
              </h3>
              <p className="text-gray-400 text-sm">
                Manage family members, tenants, and daily help permissions
                instantly.
              </p>
            </div>
          </div>

          {/* 4. Multi-Layer Security */}
          <div className="bento-card md:col-span-2 bg-[#0A0A0A] border border-white/5 rounded-4xl p-8 flex flex-col hover:border-orange-500/30 transition-colors group min-h-60">
            <div className="flex-1">
              <ShieldWall />
            </div>
            <div className="mt-4">
              <h3 className="text-xl text-white font-clash font-semibold mb-1">
                Triple-Lock Security
              </h3>
              <p className="text-gray-400 text-sm">
                Device, Cloud, and App verification layers.
              </p>
            </div>
          </div>

          {/* 5. Offline Mode */}
          <div className="bento-card md:col-span-6 bg-[#0A0A0A] border border-white/5 rounded-4xl p-20 flex flex-col md:flex-row gap-8 hover:border-orange-500/30 transition-colors group overflow-hidden items-center min-h-75 md:min-h-0">
            <div className="flex-none flex items-center justify-center bg-white/5 rounded-2xl p-4 w-full md:w-auto h-32 md:h-auto min-w-50">
              <ServerPulse />
            </div>
            <div className="flex-1 flex flex-col justify-center items-center text-center md:items-start md:text-left">
              <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center text-orange-500 mb-4">
                <WifiOff className="w-6 h-6" />
              </div>
              <h3 className="text-2xl text-white font-clash font-semibold mb-2 t md:text-left">
                Offline Ready
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed max-w-2xl">
                Internet down? No problem. The local sync engine keeps your gate
                secure 24/7. Your data is cached locally and syncs automatically
                when connection is restored.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
